<template>
  <div class="mx-auto w-full max-w-7xl px-4 pb-16 pt-10 md:px-8 md:pt-16">
    <header class="mb-8">
      <div class="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p class="text-xs uppercase tracking-[0.3em] text-[var(--accent)]">{{ $t('experiencePage.kicker') }}</p>
          <h1 class="mt-3 text-3xl font-bold text-[var(--text-primary)] md:text-5xl">{{ $t('experiencePage.title') }}</h1>
          <p class="mt-4 max-w-3xl text-[var(--text-muted)]">{{ $t('experiencePage.subtitle') }}</p>
        </div>
        <a
          :href="cvLink"
          :download="cvFileName"
          target="_blank"
          rel="noopener"
          class="mt-2 px-5 py-2.5 rounded-lg bg-[var(--accent)] text-[var(--bg-primary)] font-semibold hover:opacity-90 transition-all duration-200 border border-[var(--border)] shadow-sm whitespace-nowrap h-fit"
        >
          {{ $t('experiencePage.cvButton') }}
        </a>
      </div>
    </header>

    <section class="panel p-6">
      <h2 class="text-xl font-bold text-[var(--text-primary)]">{{ $t('experiencePage.workTitle') }}</h2>
      <div class="mt-5 grid gap-4 md:grid-cols-2">
        <article
          v-for="role in work"
          :key="role.titleKey"
          :id="role.anchorId"
          class="rounded-xl border border-[var(--border)] bg-[var(--chip-bg)] p-5"
        >
          <div class="flex items-start justify-between gap-3">
            <div>
              <h3 class="text-base font-semibold text-[var(--text-primary)]">{{ $t(role.titleKey) }}</h3>
              <p class="text-sm text-[var(--text-muted)]">{{ $t(role.roleKey) }}</p>
            </div>
            <span class="text-xs uppercase tracking-[0.2em] text-[var(--accent)]">{{ $t(role.timelineKey) }}</span>
          </div>
          <p class="mt-3 text-sm text-[var(--text-secondary)]">{{ $t(role.summaryKey) }}</p>
          <ul class="mt-3 space-y-2 text-sm text-[var(--text-secondary)]">
            <li v-for="item in role.highlights" :key="item" class="flex gap-2">
              <span class="mt-2 h-1.5 w-1.5 rounded-full bg-[var(--accent)]" />
              <span>{{ $t(item) }}</span>
            </li>
          </ul>
          <p class="mt-3 text-xs uppercase tracking-[0.2em] text-[var(--text-muted)]">{{ $t(role.stackKey) }}</p>
          <div v-if="role.references" class="mt-3 text-sm text-[var(--text-muted)]">
            <span class="font-semibold text-[var(--text-secondary)]">{{ $t('experiencePage.referencesLabel') }}:</span>
            <div class="mt-2 flex flex-wrap gap-2">
              <router-link
                v-for="ref in role.references"
                :key="ref.labelKey"
                :to="ref.to"
                class="rounded-full border border-[var(--border)] px-3 py-1 text-xs text-[var(--accent)] hover:underline"
              >
                {{ $t(ref.labelKey) }}
              </router-link>
            </div>
          </div>
        </article>
      </div>
    </section>

    <section class="mt-8 panel p-6">
      <h2 class="text-xl font-bold text-[var(--text-primary)]">{{ $t('experiencePage.stacks.title') }}</h2>
      <p class="mt-2 text-sm text-[var(--text-muted)]">{{ $t('experiencePage.stacks.subtitle') }}</p>
      <div class="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
        <div
          v-for="stack in stacks"
          :key="stack.labelKey"
          class="flex items-center gap-3 rounded-xl border border-[var(--border)] bg-[var(--chip-bg)] px-3 py-2"
        >
          <img :src="stack.icon" :alt="$t(stack.labelKey)" class="h-8 w-8" />
          <span class="text-sm text-[var(--text-secondary)]">{{ $t(stack.labelKey) }}</span>
        </div>
      </div>
    </section>

    <section class="mt-8 panel p-6">
      <h2 class="text-xl font-bold text-[var(--text-primary)]">{{ $t('experiencePage.educationTitle') }}</h2>
      <div class="mt-5 grid gap-4 md:grid-cols-2">
        <article
          v-for="study in education"
          :key="study.schoolKey"
          class="rounded-xl border border-[var(--border)] bg-[var(--chip-bg)] p-5"
        >
          <h3 class="text-base font-semibold text-[var(--text-primary)]">{{ $t(study.schoolKey) }}</h3>
          <p class="text-sm text-[var(--text-muted)]">{{ $t(study.programKey) }}</p>
          <p class="mt-3 text-sm text-[var(--text-secondary)]">{{ $t(study.summaryKey) }}</p>
          <ul class="mt-3 space-y-2 text-sm text-[var(--text-secondary)]">
            <li v-for="item in study.highlights" :key="item" class="flex gap-2">
              <span class="mt-2 h-1.5 w-1.5 rounded-full bg-[var(--accent)]" />
              <span>{{ $t(item) }}</span>
            </li>
          </ul>
          <p class="mt-3 text-xs uppercase tracking-[0.2em] text-[var(--text-muted)]">{{ $t(study.dateKey) }}</p>
        </article>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: 'ExperiencePage',
  computed: {
    cvLink() {
      return this.$t('cv.cvLink');
    },
    cvFileName() {
      return this.$t('cv.fileName');
    },
  },
  data() {
    return {
      work: [
        {
          anchorId: 'experience-cyframe',
          titleKey: 'experiencePage.work.cyframe.company',
          roleKey: 'experiencePage.work.cyframe.role',
          timelineKey: 'experiencePage.work.cyframe.timeline',
          summaryKey: 'experiencePage.work.cyframe.summary',
          highlights: [
            'experiencePage.work.cyframe.highlights.0',
            'experiencePage.work.cyframe.highlights.1',
            'experiencePage.work.cyframe.highlights.2',
          ],
          stackKey: 'experiencePage.work.cyframe.stack',
          references: [
            { to: '/ai-engineering#ai-cyframe-dev', labelKey: 'experiencePage.references.aiDev' },
            { to: '/ai-engineering#ai-eval-harness', labelKey: 'experiencePage.references.aiHarness' },
            { to: '/ai-engineering#ai-cyframe-rag', labelKey: 'experiencePage.references.aiRag' },
            { to: '/projects#project-svn-manager', labelKey: 'experiencePage.references.svnManager' },
            { to: '/projects#project-cyframe-rag', labelKey: 'projectsDetailed.cyframeRag.name' },
            { to: '/projects#project-cyframe-dev', labelKey: 'projectsDetailed.cyframeDev.name' },
            { to: '/projects#project-eval-harness', labelKey: 'projectsDetailed.evalHarness.name' },
          ],
        },
        {
          anchorId: 'experience-harbor',
          titleKey: 'experiencePage.work.harbor.company',
          roleKey: 'experiencePage.work.harbor.role',
          timelineKey: 'experiencePage.work.harbor.timeline',
          summaryKey: 'experiencePage.work.harbor.summary',
          highlights: [
            'experiencePage.work.harbor.highlights.0',
            'experiencePage.work.harbor.highlights.1',
            'experiencePage.work.harbor.highlights.2',
          ],
          stackKey: 'experiencePage.work.harbor.stack',
          references: [
            { to: '/ai-engineering#ai-louis', labelKey: 'experiencePage.references.louis' },
            { to: '/projects#project-harbor-blog', labelKey: 'experiencePage.references.harborBlog' },
            { to: '/projects#project-louis', labelKey: 'projectsDetailed.louis.name' },
          ],
        },
      ],
      stacks: [
        { labelKey: 'experiencePage.stacks.items.node', icon: require('@/assets/language/icons8-node-js-96.png') },
        { labelKey: 'experiencePage.stacks.items.express', icon: require('@/assets/language/icons8-express-js-96.png') },
        { labelKey: 'experiencePage.stacks.items.react', icon: require('@/assets/language/icons8-react-96.png') },
        { labelKey: 'experiencePage.stacks.items.vue', icon: require('@/assets/language/icons8-vue-js-96.png') },
        { labelKey: 'experiencePage.stacks.items.python', icon: require('@/assets/language/icons8-python-96.png') },
        { labelKey: 'experiencePage.stacks.items.java', icon: require('@/assets/language/icons8-java-96.png') },
        { labelKey: 'experiencePage.stacks.items.spring', icon: require('@/assets/language/icons8-spring-boot-96.png') },
        { labelKey: 'experiencePage.stacks.items.mongodb', icon: require('@/assets/language/icons8-mongo-db-96.png') },
        { labelKey: 'experiencePage.stacks.items.mysql', icon: require('@/assets/language/icons8-mysql-96.png') },
        { labelKey: 'experiencePage.stacks.items.oracle', icon: require('@/assets/language/icons8-oracle-96.png') },
        { labelKey: 'experiencePage.stacks.items.aws', icon: require('@/assets/language/icons8-aws-96.png') },
        { labelKey: 'experiencePage.stacks.items.tailwind', icon: require('@/assets/language/icons8-tailwind-css-96.png') },
        { labelKey: 'experiencePage.stacks.items.chakra', icon: require('@/assets/language/icons8-chakra-ui-96.png') },
      ],
      education: [
        {
          schoolKey: 'experiencePage.education.andre.school',
          programKey: 'experiencePage.education.andre.program',
          summaryKey: 'experiencePage.education.andre.summary',
          highlights: [
            'experiencePage.education.andre.highlights.0',
            'experiencePage.education.andre.highlights.1',
            'experiencePage.education.andre.highlights.2',
          ],
          dateKey: 'experiencePage.education.andre.date',
        },
        {
          schoolKey: 'experiencePage.education.ets.school',
          programKey: 'experiencePage.education.ets.program',
          summaryKey: 'experiencePage.education.ets.summary',
          highlights: [
            'experiencePage.education.ets.highlights.0',
            'experiencePage.education.ets.highlights.1',
            'experiencePage.education.ets.highlights.2',
          ],
          dateKey: 'experiencePage.education.ets.date',
        },
      ],
    };
  },
};
</script>
